import { useState, useEffect, useRef } from 'preact/hooks'
import { useForm, Controller } from 'react-hook-form'
import { MarkdownView } from '@/components/common/MarkdownView'
import {
  ChevronDown, ChevronRight, FileText, Upload, Link2, Sparkles,
  ArrowUpFromLine, Trash2, Download, Loader2, Plus, X, Image, RefreshCw
} from 'lucide-preact'
import type {
  KnowledgeBaseCategoryStatusDto,
  KnowledgeBaseEntryViewDto,
  KnowledgeBaseEntryUpsertDto,
  KnowledgeBaseAssetType,
  KnowledgeBaseColorPaletteDto,
} from '@/types/knowledgeBase'
import { KnowledgeBaseCategory, MarketingLanguage, type KnowledgeBaseLevel } from '@/types/knowledgeBase'
import { Select } from '@/components/common/Select'
import { FormActions } from '@/components/common/FormActions'
import { RichTextBox } from '@/components/common/RichTextBox'
import { BRAND_CLASSES } from '@/constants/theme'

interface Props {
  categoryStatus: KnowledgeBaseCategoryStatusDto
  level: KnowledgeBaseLevel
  isExpanded: boolean
  onToggle: () => void
  entry: KnowledgeBaseEntryViewDto | null
  entryLoading: boolean
  onFetchEntry: (category: string) => void
  onSave: (category: string, dto: KnowledgeBaseEntryUpsertDto) => Promise<boolean>
  onDeleteOverride: (category: string) => Promise<boolean>
  onUploadFile: (category: string, file: File, assetType?: KnowledgeBaseAssetType) => Promise<boolean>
  onDeleteFile: (category: string, fileId: string) => Promise<boolean>
  saving: boolean
  clientCompanyId: string
  /** Whether the user may edit content at this level (workspace edit capability; always true at company level, which is admin-gated server-side) */
  userCanEdit: boolean
  /** When provided, shows an AI Populate button for this category */
  onAiPopulate?: (category: string) => void
}

interface CategoryFormData {
  textContent: string
  dropdownValue: string
  links: string[]
  colorPrimary: string
  colorSecondary: string
  colorAccent: string
  colorBackground: string
  colorText: string
}

const TYPED_ASSET_SLOTS: { type: KnowledgeBaseAssetType; label: string }[] = [
  { type: 'Logo', label: 'Official Logo' },
  { type: 'Icon', label: 'Official Icon' },
  { type: 'Banner', label: 'Official Banner' },
]

const ASSET_TYPE_BADGE_COLORS: Record<KnowledgeBaseAssetType, string> = {
  Logo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  Icon: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Banner: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
}

function isValidHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value)
}

const MARKETING_LANGUAGE_OPTIONS = [
  { value: MarketingLanguage.USEnglish, label: 'US English' },
  { value: MarketingLanguage.UKEnglish, label: 'UK English' },
]

export function KnowledgeBaseCategoryRow({
  categoryStatus,
  level,
  isExpanded,
  onToggle,
  entry,
  entryLoading,
  onFetchEntry,
  onSave,
  onDeleteOverride,
  onUploadFile,
  onDeleteFile,
  saving,
  clientCompanyId,
  userCanEdit,
  onAiPopulate,
}: Props) {
  const {
    Category, CategoryDisplayName, IsPopulated, IsInherited, EffectiveLevel,
    IsAiGenerated, HasFiles, FileCount, Config,
  } = categoryStatus

  const [isEditing, setIsEditing] = useState(false)
  const [newLink, setNewLink] = useState('')
  const [editingLinks, setEditingLinks] = useState<string[]>([])
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const typedAssetInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const form = useForm<CategoryFormData>({
    mode: 'onChange',
    defaultValues: {
      textContent: '',
      dropdownValue: '',
      links: [],
      colorPrimary: '',
      colorSecondary: '',
      colorAccent: '',
      colorBackground: '',
      colorText: '',
    },
  })

  const { register, handleSubmit, reset, setValue, watch, formState: { isSubmitting, errors } } = form

  // Reset editing state when panel closes
  useEffect(() => {
    if (!isExpanded) {
      setIsEditing(false)
      setNewLink('')
      reset()
    }
  }, [isExpanded, reset])

  const handleToggle = () => {
    if (!isExpanded) {
      onFetchEntry(Category)
    }
    onToggle()
  }

  const startEditing = () => {
    if (entry) {
      const cp = entry.ColorPalette
      reset({
        textContent: entry.TextContent || '',
        dropdownValue: entry.DropdownValue || '',
        links: entry.Links || [],
        colorPrimary: cp?.Primary || '',
        colorSecondary: cp?.Secondary || '',
        colorAccent: cp?.Accent || '',
        colorBackground: cp?.Background || '',
        colorText: cp?.Text || '',
      })
      setEditingLinks([...(entry.Links || [])])
    }
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setNewLink('')
    reset()
  }

  const onSubmit = async (data: CategoryFormData) => {
    let colorPalette: KnowledgeBaseColorPaletteDto | undefined
    if (isBrandGuidelines) {
      colorPalette = {
        Primary: data.colorPrimary || null,
        Secondary: data.colorSecondary || null,
        Accent: data.colorAccent || null,
        Background: data.colorBackground || null,
        Text: data.colorText || null,
      }
    }

    const dto: KnowledgeBaseEntryUpsertDto = {
      ClientCompanyId: clientCompanyId,
      InheritFromParent: false,
      ...(Config.SupportsText ? { TextContent: data.textContent } : {}),
      ...(Config.SupportsDropdown ? { DropdownValue: data.dropdownValue } : {}),
      ...(Config.SupportsLinks ? { Links: editingLinks } : {}),
      ...(colorPalette ? { ColorPalette: colorPalette } : {}),
    }
    const success = await onSave(Category, dto)
    if (success) {
      setIsEditing(false)
      setNewLink('')
    }
  }

  const handleTypedAssetUpload = async (assetType: KnowledgeBaseAssetType, e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]
    if (file) {
      setUploadingSlot(assetType)
      await onUploadFile(Category, file, assetType)
      setUploadingSlot(null)
      target.value = ''
    }
  }

  const handleColorPickerChange = (field: keyof CategoryFormData, value: string) => {
    setValue(field, value, { shouldValidate: true, shouldDirty: true })
  }

  const handleAddLink = () => {
    const trimmed = newLink.trim()
    if (trimmed && !editingLinks.includes(trimmed)) {
      setEditingLinks([...editingLinks, trimmed])
      setNewLink('')
    }
  }

  const handleRemoveLink = (index: number) => {
    setEditingLinks(editingLinks.filter((_, i) => i !== index))
  }

  const handleFileUpload = async (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]
    if (file) {
      setUploadingSlot('general')
      await onUploadFile(Category, file)
      setUploadingSlot(null)
      target.value = ''
    }
  }

  const isBrandGuidelines = Category === KnowledgeBaseCategory.BrandGuidelines
  // Config/level may allow editing here, but the user must also have workspace edit capability.
  const canEdit = (level !== 'Campaign' || Config.CampaignOverridable) && userCanEdit
  const canRemoveOverride = level !== 'Company' && !IsInherited

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      {/* Collapsed header row */}
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {isExpanded
            ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
          }
          <div className="min-w-0">
            <span className="font-medium text-sm text-slate-900 dark:text-white block truncate">
              {CategoryDisplayName}
            </span>
            {Config.Description && (
              <span className="text-xs text-slate-500 dark:text-slate-400 block truncate mt-0.5">
                {Config.Description}
              </span>
            )}
          </div>
        </div>

        {/* Status badges */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {IsAiGenerated && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              <Sparkles className="w-3 h-3" /> AI
            </span>
          )}
          {IsInherited && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <ArrowUpFromLine className="w-3 h-3" /> {EffectiveLevel}
            </span>
          )}
          {HasFiles && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              <FileText className="w-3 h-3" /> {FileCount}
            </span>
          )}
          {IsPopulated ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium">
              Completed
            </span>
          ) : (
            <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600 flex-shrink-0" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-4 bg-slate-50/50 dark:bg-slate-800/50">
          {entryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Inheritance banner */}
              {entry?.IsInherited && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                  <ArrowUpFromLine className="w-4 h-4 flex-shrink-0" />
                  <span>Inheriting from <strong>{entry.EffectiveLevel}</strong> level</span>
                </div>
              )}

              {/* Read-only view or Edit form */}
              {!isEditing ? (
                <div className="space-y-3">
                  {/* Text content — rendered as markdown */}
                  {Config.SupportsText && entry?.TextContent && (
                    <MarkdownView className="prose-slate text-slate-700 dark:text-slate-300">{entry.TextContent}</MarkdownView>
                  )}

                  {/* Dropdown value */}
                  {Config.SupportsDropdown && entry?.DropdownValue && (
                    <div className="text-sm text-slate-700 dark:text-slate-300">
                      <span className="font-medium">Language: </span>
                      {entry.DropdownValue === MarketingLanguage.USEnglish ? 'US English' : 'UK English'}
                    </div>
                  )}

                  {/* Color palette read-only */}
                  {isBrandGuidelines && entry?.ColorPalette && (
                    <div className="space-y-2">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Color Palette</span>
                      <div className="flex flex-wrap gap-3">
                        {([
                          { key: 'Primary' as const, label: 'Primary' },
                          { key: 'Secondary' as const, label: 'Secondary' },
                          { key: 'Accent' as const, label: 'Accent' },
                          { key: 'Background' as const, label: 'Background' },
                          { key: 'Text' as const, label: 'Text' },
                        ] as const).map(({ key, label }) => {
                          const color = entry.ColorPalette?.[key]
                          if (!color) return null
                          return (
                            <div key={key} className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded border border-slate-300 dark:border-slate-600"
                                style={{ backgroundColor: color }}
                              />
                              <div className="text-xs">
                                <span className="text-slate-500 dark:text-slate-400 block">{label}</span>
                                <span className="text-slate-700 dark:text-slate-300 font-mono">{color}</span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Links */}
                  {Config.SupportsLinks && entry?.Links && entry.Links.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Links</span>
                      {entry.Links.map((link, i) => (
                        <a
                          key={i}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          <Link2 className="w-3 h-3" />
                          {link}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Typed asset slots (Logo, Icon, Banner) */}
                  {isBrandGuidelines && entry && (
                    <div className="space-y-2">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Brand Assets</span>
                      {/* Hidden file inputs — kept outside conditionals so refs persist across renders */}
                      {canEdit && !entry.IsInherited && TYPED_ASSET_SLOTS.map(({ type }) => (
                        <input
                          key={`input-${type}`}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={(el) => { typedAssetInputRefs.current[type] = el }}
                          onChange={(e) => handleTypedAssetUpload(type, e)}
                        />
                      ))}
                      {/* Row 1: Logo + Icon side by side */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {TYPED_ASSET_SLOTS.filter(s => s.type !== 'Banner').map(({ type, label }) => {
                          const existing = entry.Files.find(f => f.AssetType === type)
                          const isUploading = uploadingSlot === type
                          return (
                            <div key={type} className="relative overflow-hidden flex items-center gap-3 px-3 py-2.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                              {isUploading && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                  <div className="h-full w-1/3 bg-primary-500 rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]" />
                                </div>
                              )}
                              {existing ? (
                                <>
                                  {existing.ContentType.startsWith('image/') ? (
                                    <img src={existing.DownloadUrl} alt={label} className="w-10 h-10 object-contain rounded flex-shrink-0" />
                                  ) : (
                                    <FileText className="w-10 h-10 text-slate-400 flex-shrink-0" />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 block">{label}</span>
                                    <a href={existing.DownloadUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate block">{existing.FileName}</a>
                                  </div>
                                  {canEdit && !entry.IsInherited && (
                                    <button
                                      type="button"
                                      onClick={() => typedAssetInputRefs.current[type]?.click()}
                                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 flex-shrink-0"
                                      disabled={saving || isUploading}
                                    >
                                      <RefreshCw className="w-3 h-3" />
                                      Replace
                                    </button>
                                  )}
                                </>
                              ) : isUploading ? (
                                <>
                                  <Loader2 className="w-10 h-10 text-primary-400 flex-shrink-0 animate-spin" />
                                  <div className="min-w-0 flex-1">
                                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
                                    <span className="text-xs text-primary-500 dark:text-primary-400 block">Uploading...</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <Image className="w-10 h-10 text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
                                    <span className="text-xs text-slate-400 dark:text-slate-500 block">No file uploaded</span>
                                  </div>
                                  {canEdit && !entry.IsInherited && (
                                    <button
                                      type="button"
                                      onClick={() => typedAssetInputRefs.current[type]?.click()}
                                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 flex-shrink-0"
                                      disabled={saving}
                                    >
                                      <Upload className="w-3 h-3" />
                                      Upload
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          )
                        })}
                      </div>
                      {/* Row 2: Banner — full width */}
                      {(() => {
                        const bannerSlot = TYPED_ASSET_SLOTS.find(s => s.type === 'Banner')!
                        const existing = entry.Files.find(f => f.AssetType === 'Banner')
                        const isBannerUploading = uploadingSlot === 'Banner'
                        return (
                          <div className="relative overflow-hidden flex items-center gap-3 px-3 py-2.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                            {isBannerUploading && (
                              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                <div className="h-full w-1/3 bg-primary-500 rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]" />
                              </div>
                            )}
                            {existing ? (
                              <>
                                {existing.ContentType.startsWith('image/') ? (
                                  <img src={existing.DownloadUrl} alt={bannerSlot.label} className="h-10 max-w-[160px] object-contain rounded flex-shrink-0" />
                                ) : (
                                  <FileText className="w-10 h-10 text-slate-400 flex-shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400 block">{bannerSlot.label}</span>
                                  <a href={existing.DownloadUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate block">{existing.FileName}</a>
                                </div>
                                {canEdit && !entry.IsInherited && (
                                  <button
                                    type="button"
                                    onClick={() => typedAssetInputRefs.current['Banner']?.click()}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 flex-shrink-0"
                                    disabled={saving || isBannerUploading}
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    Replace
                                  </button>
                                )}
                              </>
                            ) : isBannerUploading ? (
                              <>
                                <Loader2 className="w-10 h-10 text-primary-400 flex-shrink-0 animate-spin" />
                                <div className="min-w-0 flex-1">
                                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{bannerSlot.label}</span>
                                  <span className="text-xs text-primary-500 dark:text-primary-400 block">Uploading...</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <Image className="w-10 h-10 text-slate-300 dark:text-slate-600 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{bannerSlot.label}</span>
                                  <span className="text-xs text-slate-400 dark:text-slate-500 block">No file uploaded</span>
                                </div>
                                {canEdit && !entry.IsInherited && (
                                  <button
                                    type="button"
                                    onClick={() => typedAssetInputRefs.current['Banner']?.click()}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 flex-shrink-0"
                                    disabled={saving}
                                  >
                                    <Upload className="w-3 h-3" />
                                    Upload
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                  )}

                  {/* Files */}
                  {Config.SupportsUpload && entry?.Files && entry.Files.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Files</span>
                      {entry.Files.map((file) => (
                        <div key={file.FileId} className="flex items-center justify-between gap-2 px-3 py-2 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{file.FileName}</span>
                            {file.AssetType && (
                              <span className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded-full font-medium ${ASSET_TYPE_BADGE_COLORS[file.AssetType]}`}>
                                {file.AssetType}
                              </span>
                            )}
                            <span className="text-xs text-slate-400 flex-shrink-0">
                              {(file.FileSizeBytes / 1024 / 1024).toFixed(1)} MB
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <a
                              href={file.DownloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                            {canEdit && !entry.IsInherited && (
                              <button
                                type="button"
                                onClick={() => onDeleteFile(Category, file.FileId)}
                                className="p-1 text-slate-400 hover:text-red-500"
                                title="Delete file"
                                disabled={saving}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty state */}
                  {!IsPopulated && !entry?.TextContent && !entry?.DropdownValue && (
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">No content yet</p>
                  )}

                  {/* Action buttons */}
                  {canEdit && (
                    <div className="flex items-center gap-2 pt-2">
                      <button
                        type="button"
                        onClick={startEditing}
                        className="text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                      >
                        {IsPopulated ? 'Edit' : 'Add Content'}
                      </button>

                      {Config.SupportsUpload && !entry?.IsInherited && (
                        <>
                          <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleFileUpload}
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            disabled={saving}
                          >
                            <Upload className="w-3.5 h-3.5" />
                            Upload File
                          </button>
                        </>
                      )}

                      {onAiPopulate && (
                        <button
                          type="button"
                          onClick={() => onAiPopulate(Category)}
                          className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                          disabled={saving}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          AI Populate
                        </button>
                      )}

                      {canRemoveOverride && (
                        <button
                          type="button"
                          onClick={() => onDeleteOverride(Category)}
                          className="text-sm px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-auto"
                          disabled={saving}
                        >
                          Revert to {level === 'Campaign' ? 'Workspace' : 'Company'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Edit mode — uses react-hook-form + common components */
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {Config.SupportsText && (
                    <Controller
                      name="textContent"
                      control={form.control}
                      render={({ field }) => (
                        <RichTextBox
                          name="textContent"
                          placeholder={`Enter ${CategoryDisplayName.toLowerCase()} content...`}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                  )}

                  {Config.SupportsDropdown && (
                    <Select
                      label="Marketing Language"
                      options={MARKETING_LANGUAGE_OPTIONS}
                      placeholder="Select language..."
                      {...register('dropdownValue')}
                    />
                  )}

                  {isBrandGuidelines && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Color Palette</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {([
                          { field: 'colorPrimary' as const, label: 'Primary', placeholder: '#1E40AF' },
                          { field: 'colorSecondary' as const, label: 'Secondary', placeholder: '#9333EA' },
                          { field: 'colorAccent' as const, label: 'Accent', placeholder: '#F59E0B' },
                          { field: 'colorBackground' as const, label: 'Background', placeholder: '#FFFFFF' },
                          { field: 'colorText' as const, label: 'Text', placeholder: '#111827' },
                        ]).map(({ field, label, placeholder }) => (
                          <div key={field}>
                            <label className="block text-xs font-medium mb-1 text-slate-600 dark:text-slate-400">{label}</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                aria-label={`${label} color`}
                                value={watch(field) || placeholder}
                                onChange={(e) => handleColorPickerChange(field, (e.target as HTMLInputElement).value)}
                                className="w-8 h-8 rounded border border-slate-300 dark:border-slate-600 cursor-pointer"
                              />
                              <input
                                type="text"
                                className={`px-3 py-1.5 border rounded text-sm w-28 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:border-transparent ${
                                  errors[field]
                                    ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                                    : `border-slate-300 dark:border-slate-700 ${BRAND_CLASSES.FOCUS.PRIMARY}`
                                }`}
                                placeholder={placeholder}
                                {...register(field, {
                                  validate: (v) => !v || isValidHexColor(v) || 'Invalid hex color',
                                })}
                              />
                            </div>
                            {errors[field] && (
                              <p className="text-xs text-red-600 mt-1">{String(errors[field]?.message)}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Config.SupportsLinks && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Links</label>
                      <div className="space-y-2">
                        {editingLinks.map((link, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate">{link}</span>
                            <button type="button" onClick={() => handleRemoveLink(i)} className="text-red-500 hover:text-red-700">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center gap-2">
                          <input
                            type="url"
                            value={newLink}
                            onInput={(e) => setNewLink((e.target as HTMLInputElement).value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLink() } }}
                            placeholder="https://..."
                            className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <button type="button" onClick={handleAddLink} className="p-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <FormActions
                    isSubmitting={saving || isSubmitting}
                    isSaveDisabled={saving || isSubmitting}
                    onCancel={cancelEditing}
                    saveText={saving ? 'Saving...' : 'Save'}
                    className="px-0 bg-transparent border-0"
                  />
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
